import React, { useMemo } from "react";

const SQL_KEYWORDS = new Set([
  "select",
  "from",
  "where",
  "join",
  "left",
  "right",
  "inner",
  "outer",
  "full",
  "on",
  "and",
  "or",
  "not",
  "as",
  "case",
  "when",
  "then",
  "else",
  "end",
  "cast",
  "convert",
  "distinct",
  "group",
  "by",
  "order",
  "having",
  "union",
  "all",
  "is",
  "null",
  "like",
  "in",
  "exists",
  "between",
  "over",
  "partition",
  "into"
]);

type Token = {
  text: string;
  className?: string;
};

const tokenizeSql = (value: string): Token[] => {
  const tokens: Token[] = [];
  const pattern =
    /(--.*$|\/\*[\s\S]*?\*\/|'(?:''|[^'])*'|\b\d+(?:\.\d+)?\b|\b[a-zA-Z_][a-zA-Z0-9_]*\b|[(),.*+\-/%=<>!]+)/gm;

  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(value)) !== null) {
    if (match.index > cursor) {
      tokens.push({ text: value.slice(cursor, match.index) });
    }

    const text = match[0];
    const lowered = text.toLowerCase();
    let className: string | undefined;

    if (text.startsWith("--") || text.startsWith("/*")) {
      className = "sql-token-comment";
    } else if (text.startsWith("'")) {
      className = "sql-token-string";
    } else if (/^\d/.test(text)) {
      className = "sql-token-number";
    } else if (/^[a-zA-Z_]/.test(text)) {
      className = SQL_KEYWORDS.has(lowered) ? "sql-token-keyword" : "sql-token-name";
    } else if (/^[(),.*+\-/%=<>!]+$/.test(text)) {
      className = "sql-token-operator";
    }

    tokens.push({ text, className });
    cursor = match.index + text.length;
  }

  if (cursor < value.length) {
    tokens.push({ text: value.slice(cursor) });
  }

  return tokens;
};

export function SqlPreview({
  value,
  placeholder,
  wrap = false,
  showLineNumbers = false
}: {
  value: string;
  placeholder?: string;
  wrap?: boolean;
  showLineNumbers?: boolean;
}): JSX.Element {
  const tokens = useMemo(() => tokenizeSql(value), [value]);
  const lineCount = useMemo(() => value.split("\n").length, [value]);
  const hasContent = value.trim().length > 0;

  const preview = (
    <pre className={`tw-m-0 tw-max-w-full tw-font-mono tw-text-slate-700 ${wrap ? "tw-overflow-x-hidden tw-whitespace-pre-wrap tw-break-words" : "tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap"}`}>
      {hasContent ? (
        tokens.map((token, index) => (
          <span key={`${token.text}-${index}`} className={token.className}>
            {token.text}
          </span>
        ))
      ) : (
        <span className="tw-text-slate-500">{placeholder ?? "No transform"}</span>
      )}
    </pre>
  );

  return (
    <div className={`tw-w-full tw-min-w-0 tw-max-w-full ${wrap ? "tw-overflow-x-hidden tw-text-xs" : "tw-overflow-hidden tw-text-[11px]"} tw-leading-5`}>
      <style>
        {`
          .sql-token-keyword { color: #0f4c81; font-weight: 600; }
          .sql-token-string { color: #0f766e; }
          .sql-token-number { color: #7c3aed; }
          .sql-token-comment { color: #64748b; font-style: italic; }
          .sql-token-name { color: #1e293b; }
          .sql-token-operator { color: #475569; }
        `}
      </style>
      {showLineNumbers && hasContent ? (
        <div className="tw-grid tw-grid-cols-[3.25rem_minmax(0,1fr)] tw-gap-3">
          <pre
            aria-hidden="true"
            className="tw-m-0 tw-select-none tw-border-r tw-border-slate-200 tw-pr-3 tw-text-right tw-font-mono tw-text-slate-400"
          >
            {Array.from({ length: lineCount }, (_, index) => index + 1).join("\n")}
          </pre>
          <div className="tw-min-w-0">{preview}</div>
        </div>
      ) : (
        preview
      )}
    </div>
  );
}
